import React, { useCallback, useMemo, useState } from "react";
import { Button, Card, Input, Spin, Tree, message } from "antd";
import type { DataNode } from "antd/es/tree";
import { actionPlanV2Api, type AiListCongViecResponse, type AiScopeTreeResponse } from "@/services/action-plan-v2";
import { unwrapApiResponse } from "@/components/common/ActionPlanV2/actionPlanAiConfirm";
import { fetchActionPlanAiTaskListPage } from "@/components/common/ActionPlanV2/actionPlanAiTaskList";
import { ACTION_PLAN_V2 } from "@/components/common/ActionPlanV2/constants";
import { ACTION_PLAN_AI_STATUSES } from "@/components/common/ActionPlanV2/actionPlanAiStatus";
import type {
  ActionPlanAiScopeEmployee,
  ActionPlanAiScopeListState,
} from "@/types/action-plan-v2/ActionPlanAiScopeList";
import type {
  ActionPlanAiTaskListPayload,
  ActionPlanAiTaskListState,
  ActionPlanAiViewParams,
} from "@/types/action-plan-v2/ActionPlanAiTaskList";
import cardStyles from "./ActionPlanTaskListCard.module.css";
import apCard from "./apCard.module.css";

type Props = {
  state: ActionPlanAiScopeListState;
  /** Bấm một trạng thái → hiện thẻ danh sách việc (chỉ xem) bên dưới. */
  onShowTaskList: (next: ActionPlanAiTaskListState) => void;
};

type Selected = { kind: "department" | "employee"; id: string; ten: string };

type Summary = {
  tongViec: number;
  counts: Record<string, number>;
  groups: Array<{ id: string; ten?: string; soViec: number; treHan: number }>;
};

function buildTree(list: ActionPlanAiScopeListState["list"]): DataNode[] {
  const byParent = new Map<string, ActionPlanAiScopeListState["list"]["phongBans"]>();
  const ids = new Set(list.phongBans.map((d) => d.id));
  for (const d of list.phongBans) {
    const key = d.parentId && ids.has(d.parentId) ? d.parentId : "";
    byParent.set(key, [...(byParent.get(key) ?? []), d]);
  }
  const walk = (parent: string): DataNode[] =>
    (byParent.get(parent) ?? []).map((d) => ({
      key: d.id,
      title: d.ten || d.id,
      children: walk(d.id),
    }));
  return walk("");
}

export const ActionPlanScopePickerCard: React.FC<Props> = ({ state, onShowTaskList }) => {
  const { list } = state;
  const scopeReq = useMemo(
    () => ({
      idChoice: String(list.idChoice || "").trim(),
      year: list.year ?? new Date().getFullYear(),
      phanHe: list.phanHe ?? ACTION_PLAN_V2.PHAN_HE,
    }),
    [list.idChoice, list.phanHe, list.year],
  );
  const treeData = useMemo(() => buildTree(list), [list]);
  const [selected, setSelected] = useState<Selected | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [employees, setEmployees] = useState<ActionPlanAiScopeEmployee[]>(list.nhanViens ?? []);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const viewParams = (sel: Selected): ActionPlanAiViewParams =>
    sel.kind === "department" ? { boPhanId: sel.id } : { nguoiPhuTrachId: sel.id };

  const loadSummary = useCallback(
    async (sel: Selected) => {
      setSelected(sel);
      setSummary(null);
      setLoading(true);
      try {
        const [rawSum, rawEmp] = await Promise.all([
          actionPlanV2Api.aiStatusSummary({ ...scopeReq, ...viewParams(sel) }),
          sel.kind === "department"
            ? actionPlanV2Api.aiScopeTree({ ...scopeReq, boPhanId: sel.id })
            : Promise.resolve(null),
        ]);
        const res = unwrapApiResponse<AiListCongViecResponse>(rawSum);
        if (!res?.success || res.data == null) throw new Error(res?.message || "Không tải được thống kê.");
        const data = res.data as AiListCongViecResponse & {
          nhomTheoNguoiPhuTrach?: Array<{ nguoiPhuTrachId?: string; ten?: string; soViec?: number; treHan?: number }>;
        };
        setSummary({
          tongViec: data.tongViec ?? 0,
          counts: data.thongKeTrangThai ?? {},
          groups: (data.nhomTheoNguoiPhuTrach ?? [])
            .filter((g) => g.nguoiPhuTrachId)
            .map((g) => ({ id: String(g.nguoiPhuTrachId), ten: g.ten, soViec: g.soViec ?? 0, treHan: g.treHan ?? 0 })),
        });
        if (rawEmp) {
          const emp = unwrapApiResponse<AiScopeTreeResponse>(rawEmp);
          if (emp?.success && emp.data) setEmployees(emp.data.nhanViens ?? []);
        }
      } catch (e) {
        message.error(e instanceof Error ? e.message : "Không tải được thống kê.");
      } finally {
        setLoading(false);
      }
    },
    [scopeReq],
  );

  const searchEmployees = useCallback(
    async (q: string) => {
      setLoading(true);
      try {
        const raw = await actionPlanV2Api.aiScopeTree({ ...scopeReq, query: q });
        const res = unwrapApiResponse<AiScopeTreeResponse>(raw);
        if (!res?.success || !res.data) throw new Error(res?.message || "Không tìm được nhân viên.");
        setEmployees(res.data.nhanViens ?? []);
      } catch (e) {
        message.error(e instanceof Error ? e.message : "Không tìm được nhân viên.");
      } finally {
        setLoading(false);
      }
    },
    [scopeReq],
  );

  const openTaskList = async (trangThai: string) => {
    if (!selected) return;
    const params: ActionPlanAiViewParams = { ...viewParams(selected), trangThai };
    const seed: ActionPlanAiTaskListPayload = {
      kind: "task_picker",
      intent: "browse",
      page: 1,
      pageSize: 8,
      total: 0,
      totalPages: 0,
      items: [],
      view: {
        scope: selected.kind,
        readOnly: true,
        tenBoPhan: selected.kind === "department" ? selected.ten : undefined,
        tenNhanVien: selected.kind === "employee" ? selected.ten : undefined,
        params,
      },
      idChoice: scopeReq.idChoice,
      year: scopeReq.year,
      phanHe: scopeReq.phanHe,
    };
    setListLoading(true);
    try {
      const next = await fetchActionPlanAiTaskListPage(seed, 1, undefined, params);
      onShowTaskList({ list: next, loading: false });
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Không tải được danh sách việc.");
    } finally {
      setListLoading(false);
    }
  };

  if (list.scope === "self") {
    return (
      <Card size="small" title="Phạm vi xem" className={`${cardStyles.card} ${apCard.card}`}>
        <div className={cardStyles.meta}>Bạn chỉ xem được công việc của chính mình.</div>
      </Card>
    );
  }

  return (
    <Card size="small" title="Chọn phòng ban / nhân viên" className={`${cardStyles.card} ${apCard.card}`}>
      {treeData.length > 0 ? (
        <Tree
          treeData={treeData}
          height={200}
          defaultExpandedKeys={treeData.map((n) => n.key)}
          selectedKeys={selected?.kind === "department" ? [selected.id] : []}
          onSelect={(keys, info) => {
            const id = String(keys[0] ?? "");
            if (id) void loadSummary({ kind: "department", id, ten: String(info.node.title ?? id) });
          }}
          className="!mb-2 !text-[13px]"
        />
      ) : null}

      <Input.Search
        size="small"
        allowClear
        placeholder="Tìm nhân viên theo tên…"
        onSearch={(val) => val.trim() && void searchEmployees(val.trim())}
        loading={loading && !selected}
        className="!mb-2"
      />
      {employees.length > 0 ? (
        <div className={cardStyles.groups}>
          {employees.slice(0, 20).map((e) => (
            <button
              key={e.id}
              type="button"
              className={cardStyles.groupChip}
              disabled={loading}
              onClick={() => void loadSummary({ kind: "employee", id: e.id, ten: e.ten || e.id })}
              title={e.tenBoPhan || undefined}
            >
              {e.ten || e.id}
            </button>
          ))}
        </div>
      ) : null}

      {selected ? (
        <div className={cardStyles.row}>
          <div className={cardStyles.title}>
            {selected.kind === "department" ? `Phòng ${selected.ten} (gồm phòng con)` : selected.ten}
          </div>
          {loading ? (
            <Spin size="small" />
          ) : summary ? (
            <>
              <div className={cardStyles.meta}>Tổng {summary.tongViec} việc</div>
              <div className={cardStyles.filters}>
                {ACTION_PLAN_AI_STATUSES.map((s) => (
                  <Button
                    key={s.value}
                    size="small"
                    danger={s.danger && (summary.counts[s.value] ?? 0) > 0}
                    disabled={listLoading || (summary.counts[s.value] ?? 0) === 0}
                    onClick={() => void openTaskList(s.value)}
                  >
                    {s.label}: {summary.counts[s.value] ?? 0}
                  </Button>
                ))}
              </div>
              {selected.kind === "department" && summary.groups.length > 1 ? (
                <div className={cardStyles.groups}>
                  {summary.groups.slice(0, 6).map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      className={cardStyles.groupChip}
                      disabled={loading}
                      onClick={() => void loadSummary({ kind: "employee", id: g.id, ten: g.ten || g.id })}
                      title="Xem riêng người này"
                    >
                      {g.ten || g.id} · {g.soViec} việc
                      {g.treHan > 0 ? <span className={cardStyles.groupLate}> · {g.treHan} trễ</span> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : (
        <div className={cardStyles.meta}>Chọn phòng ban hoặc nhân viên để xem số việc theo trạng thái.</div>
      )}
    </Card>
  );
};
